// import './styles.css';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { AnimatePresence, LayoutGroup, Reorder } from 'framer-motion';
import { ReorderItem } from './ReorderItem';
import PropTypes from 'prop-types';
import ObjectID from 'bson-objectid';
import ItemDetailDialog from './ItemDetailDialog';
import ArgonButton from 'components/ArgonButton';
import ArgonBox from 'components/ArgonBox';

import 'assets/css/reorderWorkouts.css';

//! this component offers a list that can be re-ordered and edited
//! you provide custom components for the list items and the add/edit item dialog
//! you also provide the text for the add and edit buttons

export default function ReorderListCustomComponents({
  unorderedItems = [],
  onReorder,
  onItemsUpdated,
  onItemClicked,
  addItemText = 'Add Item',
  editItemText = 'Edit Item',
  editItemComponent,
  addItemComponent,
  listItemComponent,
  selectColor,
  dialogSize = 'sm',
  addButtonStyle = 'text',
  addButtonColor = 'dark',
  addButtonSize = 'small'
}) {
  const [showEditItemDialog, setShowEditItemDialog] = React.useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    // Initialize items with IDs if they don't have them
    const initializedItems = unorderedItems.map((item) => ({
      ...item,
      _id: item._id || item.id || new ObjectID().toHexString()
    }));
    setItems(initializedItems);
  }, [unorderedItems]);

  const handleItemsUpdated = (updatedItems) => {
    onItemsUpdated?.(updatedItems);
  };

  const handleAddItemButtonClicked = (event) => {
    event?.preventDefault();
    setShowAddItemDialog(true);
  };

  const handleItemDeleted = (item) => {
    const updatedItems = items.filter((i) => i._id !== item._id);
    setItems(updatedItems);
    handleItemsUpdated(updatedItems);
  };
  const handleItemClicked = (item) => {
    console.log(
      'ReorderListCustomComponents: handleItemClicked in HOC called',
      item
    );
    // Wichtig: Wir erstellen eine Kopie des Objekts, um sicherzustellen,
    // dass React die Änderung erkennt und die Komponente neu rendert
    setSelectedItem({ ...item });
    setShowEditItemDialog(true);
  };

  const handleItemUpdated = (item) => {
    console.log(
      'ReorderListCustomComponents: handleItemUpdated in HOC called',
      item
    );

    // Check if the item already exists in the list
    const itemExists = items.some((i) => i._id === item._id);

    let updatedItems;
    if (itemExists) {
      // Update existing item
      updatedItems = items.map((i) => {
        if (i._id === item._id) return item;
        return i;
      });
    } else {
      // Add as new item
      updatedItems = [...items, item];
    }

    setItems(updatedItems);
    handleItemsUpdated(updatedItems);
    setShowEditItemDialog(false);
  };

  const handleItemAdded = (item) => {
    // If the item already has an _id, use it; otherwise generate a new one
    const newItem = {
      ...item,
      // id: item.id || item._id || new ObjectID().toHexString(),
      _id: item._id || new ObjectID().toHexString()
    };

    // Add the new item to the list
    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    handleItemsUpdated(updatedItems);
    setShowAddItemDialog(false);

    // Log for debugging
    console.log('Item added:', newItem);
  };

  const handleItemsReordered = (items) => {
    setItems(items);
    handleItemsUpdated(items);
    onReorder?.(items);
  };

  return (
    <ArgonBox>
      <LayoutGroup>
        <Reorder.Group axis="y" onReorder={handleItemsReordered} values={items}>
          <AnimatePresence>
            {items.map((item) => (
              <ReorderItem
                key={item._id}
                item={item}
                onItemClicked={handleItemClicked}
                onItemDeleted={() => handleItemDeleted(item)}
                listItemComponent={listItemComponent}
              />
            ))}
          </AnimatePresence>
        </Reorder.Group>
      </LayoutGroup>
      <ArgonButton
        color={addButtonColor}
        variant={addButtonStyle}
        size={addButtonSize}
        onClick={handleAddItemButtonClicked}>
        {addItemText}
      </ArgonButton>

      <ItemDetailDialog
        open={showAddItemDialog}
        addNew
        dialogTitle={addItemText}
        selectColor={selectColor}
        onClose={() => setShowAddItemDialog(false)}
        onChange={handleItemAdded}
        component={addItemComponent}
        size={dialogSize}
      />

      <ItemDetailDialog
        open={showEditItemDialog}
        item={selectedItem}
        dialogTitle={editItemText}
        selectColor={selectColor}
        color={selectedItem?.color}
        onClose={() => setShowEditItemDialog(false)}
        onChange={handleItemUpdated}
        onRemove={() => handleItemDeleted(selectedItem)}
        component={editItemComponent}
        size={dialogSize}
      />
    </ArgonBox>
  );
}

ReorderListCustomComponents.propTypes = {
  unorderedItems: PropTypes.arrayOf(
    PropTypes.shape({
      // id: PropTypes.string,
      _id: PropTypes.string,
      label: PropTypes.string,
      color: PropTypes.string
    })
  ),
  onReorder: PropTypes.func,
  onItemsUpdated: PropTypes.func,
  onItemClicked: PropTypes.func,
  addItemText: PropTypes.string,
  editItemText: PropTypes.string,
  editItemComponent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType
  ]),
  addItemComponent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType
  ]),
  listItemComponent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType
  ]),
  selectColor: PropTypes.bool,
  dialogSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl'])
};
